import { useMemo } from "react"
import type { Endpoint } from "@/types/endpoint"

// Full-detail modal for a single endpoint's error history.
//   - Larger multi-line chart, one polyline per phase.
//   - Phase totals.
//   - Recent errors table (timestamp, phase, status, message, source_key).

const PHASE_COLORS: Record<string, string> = {
  timeout:           "#f59e0b",
  connection_error:  "#ef4444",
  http_5xx:          "#dc2626",
  http_4xx:          "#a855f7",
  parse_error:       "#06b6d4",
  other:             "#64748b",
}

type Props = {
  endpoint: Endpoint
  onClose: () => void
}

export function EndpointErrorModal({ endpoint, onClose }: Props) {
  const days = useMemo(() => Object.keys(endpoint.errorsByDayByPhase).sort(), [endpoint.errorsByDayByPhase])

  // Pre-compute series (one number array per phase, length = days.length).
  const series = useMemo(() => {
    const phases = new Set<string>()
    days.forEach((d) => Object.keys(endpoint.errorsByDayByPhase[d] ?? {}).forEach((p) => phases.add(p)))
    return Array.from(phases).map((phase) => ({
      phase,
      values: days.map((d) => endpoint.errorsByDayByPhase[d]?.[phase] ?? 0),
    }))
  }, [days, endpoint.errorsByDayByPhase])

  const yMax = Math.max(1, ...series.flatMap((s) => s.values))

  const chartWidth = 720
  const chartHeight = 240

  return (
    <div
      role="dialog"
      aria-modal
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-card border border-border bg-app-panel shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="text-sm text-app-muted">Endpoint errors — last 30 days</div>
            <div className="text-lg font-semibold text-app-text">{endpoint.name}</div>
            <div className="font-mono text-xs text-app-muted">{endpoint.key}</div>
          </div>
          <button type="button" onClick={onClose}
            className="rounded-control border border-border px-3 py-1.5 text-sm text-app-text hover:bg-white/5">
            Close
          </button>
        </div>

        <div className="max-h-[calc(90vh-100px)] space-y-4 overflow-y-auto p-5">
          {/* Multi-line chart */}
          <div className="rounded-card border border-border bg-app-bg p-4">
            <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
              {series.length === 0 ? (
                <span className="text-app-muted">No errors recorded.</span>
              ) : (
                series.map((s) => (
                  <span key={s.phase} className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-3 rounded-sm" style={{ background: PHASE_COLORS[s.phase] ?? PHASE_COLORS.other }} />
                    <span className="font-mono text-app-text">{s.phase}</span>
                    <span className="text-app-muted">{s.values.reduce((a, b) => a + b, 0)}</span>
                  </span>
                ))
              )}
            </div>

            {series.length > 0 && (
              <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="overflow-visible">
                {/* Y gridlines */}
                {[0.25, 0.5, 0.75, 1].map((frac) => (
                  <line key={frac} x1={0} x2={chartWidth} y1={chartHeight * (1 - frac)} y2={chartHeight * (1 - frac)}
                    stroke="currentColor" strokeWidth={0.4} className="text-app-muted opacity-20" />
                ))}
                {/* Y labels */}
                {[0, 0.5, 1].map((frac) => (
                  <text key={frac} x={4} y={chartHeight * (1 - frac) + 3} className="fill-app-muted text-[10px]">
                    {Math.round(yMax * frac)}
                  </text>
                ))}
                {/* X labels — show first / middle / last day */}
                {[0, Math.floor(days.length / 2), days.length - 1].map((i) => (
                  <text key={i} x={(i / Math.max(1, days.length - 1)) * chartWidth} y={chartHeight + 14}
                    textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"}
                    className="fill-app-muted text-[10px]">
                    {days[i]}
                  </text>
                ))}
                {/* Series lines */}
                {series.map((s) => {
                  const stepX = chartWidth / Math.max(1, days.length - 1)
                  const path = s.values.map((v, i) => {
                    const x = i * stepX
                    const y = chartHeight - (v / yMax) * chartHeight
                    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`
                  }).join(" ")
                  return (
                    <path key={s.phase} d={path} fill="none"
                      stroke={PHASE_COLORS[s.phase] ?? PHASE_COLORS.other}
                      strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  )
                })}
              </svg>
            )}
          </div>

          {/* Phase breakdown chips */}
          {Object.keys(endpoint.errorStats.phaseBreakdown).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(endpoint.errorStats.phaseBreakdown).map(([phase, count]) => (
                <span key={phase}
                  className="rounded-chip border border-border bg-white/5 px-2 py-1 text-xs">
                  <span className="font-mono text-app-text">{phase}</span>
                  <span className="ml-1 text-app-muted">{count}</span>
                </span>
              ))}
            </div>
          )}

          {/* Recent errors table */}
          {endpoint.errorStats.recentErrors.length > 0 && (
            <div className="rounded-card border border-border bg-app-bg">
              <div className="border-b border-border px-4 py-2 text-xs uppercase tracking-wide text-app-muted">
                Recent errors
              </div>
              <div className="divide-y divide-border">
                {endpoint.errorStats.recentErrors.map((r) => (
                  <div key={r.id} className="grid grid-cols-[140px_120px_60px_1fr_120px] gap-3 px-4 py-2 text-xs">
                    <span className="text-app-muted">{new Date(r.occurredAt).toLocaleString()}</span>
                    <span className="font-mono text-app-text">{r.phase}</span>
                    <span className="text-app-muted">{r.httpStatus ?? "—"}</span>
                    <span className="truncate font-mono text-app-text" title={r.errorMessage ?? ""}>{r.errorMessage ?? r.errorClass ?? "—"}</span>
                    <span className="truncate text-right font-mono text-app-muted">{r.sourceKey ?? "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
